import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

const useQuery = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const setQuery = useCallback(
    (key: string, value: any) => {
      setSearchParams((prevSearchParams) => {
        const newSearchParams = new URLSearchParams(prevSearchParams)
        newSearchParams.set(
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        )
        return newSearchParams
      })
    },
    [setSearchParams],
  )

  // Applies several query changes in ONE navigation. A `null`/`undefined` value
  // deletes that key — this lets callers set and remove keys atomically. (React
  // Router's setSearchParams reads a stale base per call, so multiple separate
  // set/remove calls in one handler would clobber each other — hence the single
  // updater here.)
  const setMultipleQueries = (queryParams: Record<string, any>) => {
    setSearchParams((prevSearchParams) => {
      const newSearchParams = new URLSearchParams(prevSearchParams)

      Object.entries(queryParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          newSearchParams.delete(key)
        } else {
          newSearchParams.set(
            key,
            typeof value === 'object' ? JSON.stringify(value) : value,
          )
        }
      })

      return newSearchParams
    })
  }

  const getQuery = useCallback(
    (key: string) => {
      const value = searchParams.get(key)
      try {
        return value ? JSON.parse(value) : null
      } catch (error) {
        return value
      }
    },
    [searchParams],
  )

  const removeQuery = useCallback(
    (key: string) => {
      setSearchParams((prevSearchParams) => {
        const newSearchParams = new URLSearchParams(prevSearchParams)
        newSearchParams.delete(key)
        return newSearchParams
      })
    },
    [setSearchParams],
  )

  const getAllQueries = useCallback(() => {
    const queries: any = {}

    searchParams.forEach((value, key) => {
      try {
        queries[key] = JSON.parse(value)
      } catch (error) {
        queries[key] = value
      }
    })
    return queries
  }, [searchParams])

  return { setQuery, getQuery, removeQuery, getAllQueries, setMultipleQueries }
}

export default useQuery
